import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactManagedDragable from 'react-managed-draggable';
import * as DropModel from './drop-model';
import { StaticUtilities } from './static-utilities';
export class Floaty extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            currentMousePosition: null,
            dropResolutions: [],
            rootDropArea: null
        };
        this.portal = document.createElement('div');
        this.dropResolutions = new Map();
        this.eventTarget = null;
        this.raf = undefined;
        this.updateRootDropArea = () => {
            const node = ReactDOM.findDOMNode(this);
            if (node instanceof Element) {
                const dropArea = DropModel.computeDropArea(node);
                if (!this.state.rootDropArea || !DropModel.dropAreasEqual(this.state.rootDropArea, dropArea)) {
                    this.setState({ rootDropArea: dropArea });
                }
            }
            this.raf = window.requestAnimationFrame(this.updateRootDropArea);
        };
        this.onRowOrColumnUpdateFractions = (rowOrColumn, index1, fraction1, index2, fraction2) => {
            const path = this.findPath(rowOrColumn, this.props.state.layout);
            if (!path) {
                if (rowOrColumn.type === 'column') {
                    throw new Error('Column not found.');
                }
                else {
                    throw new Error('Row not found.');
                }
            }
            const items = rowOrColumn.items.slice();
            items[index1] = Object.assign(Object.assign({}, items[index1]), { fraction: fraction1 });
            items[index2] = Object.assign(Object.assign({}, items[index2]), { fraction: fraction2 });
            const newRowOrColumn = Object.assign(Object.assign({}, rowOrColumn), { items });
            this.replaceInPath(newRowOrColumn, path);
            this.onLayoutChange(path[path.length - 1]);
        };
        this.registerDropResolutions = (key, dropResolutions) => {
            this.dropResolutions.set(key, dropResolutions);
            this.updateDropResolutions();
        };
        this.unregisterDropResolutions = (key) => {
            this.dropResolutions.delete(key);
            this.updateDropResolutions();
        };
        this.updateColumnFractions = this.onRowOrColumnUpdateFractions;
        this.updateRowFractions = this.onRowOrColumnUpdateFractions;
        this.activateStackItem = (stackItem) => {
            const stack = this.findStack(stackItem);
            if (!stack) {
                throw new Error(`StackItem ${stackItem.key} not found.`);
            }
            const index = stack.items.indexOf(stackItem);
            const path = this.findPath(stack, this.props.state.layout);
            if (!path) {
                throw new Error('Stack not found.');
            }
            const newStack = Object.assign(Object.assign({}, stack), { active: index });
            this.replaceInPath(newStack, path);
            this.onLayoutChange(path[path.length - 1]);
        };
        this.closeTab = (stackItem) => {
            const stack = this.findStack(stackItem);
            if (!stack) {
                throw new Error(`StackItem ${stackItem.key} not found.`);
            }
            const index = stack.items.indexOf(stackItem);
            const path = this.findPath(stack, this.props.state.layout);
            if (!path) {
                throw new Error('Stack not found.');
            }
            const items = stack.items.slice();
            items.splice(index, 1);
            const newStack = Object.assign(Object.assign({}, stack), { items, active: Math.min(items.length - 1, stack.active) });
            this.replaceInPath(newStack, path);
            this.onLayoutChange(path[path.length - 1]);
        };
        this.replaceItem = (stackItem, item, key) => {
            const stack = this.findStack(stackItem);
            if (!stack) {
                throw new Error(`StackItem ${stackItem.key} not found.`);
            }
            const index = stack.items.indexOf(stackItem);
            const path = this.findPath(stack, this.props.state.layout);
            if (!path) {
                throw new Error('Stack not found.');
            }
            const newItems = stack.items.slice();
            newItems[index] = {
                key: key === undefined ? newItems[index].key : key,
                item
            };
            const newStack = Object.assign(Object.assign({}, stack), { items: newItems });
            this.replaceInPath(newStack, path);
            this.onLayoutChange(path[path.length - 1]);
        };
        this.handleMove = (event) => {
            if (this.props.state.floating) {
                event.preventDefault();
                const position = ReactManagedDragable.getPosition(event);
                this.setState({ currentMousePosition: position });
            }
        };
        this.handleUp = (event) => {
            if (!this.props.state.floating) {
                return;
            }
            const position = event instanceof MouseEvent
                ? ReactManagedDragable.getPosition(event)
                : this.state.currentMousePosition;
            if (!position) {
                return;
            }
            const resolution = this.getCandidateDropResolution(position);
            if (!resolution) {
                return;
            }
            let path;
            if (resolution.type === 'root') {
                path = [{ type: 'stack', active: 0, items: [this.props.state.floating] }];
            }
            else {
                path = this.findPath(resolution.stack, this.props.state.layout);
                if (!path) {
                    throw new Error('Stack not found.');
                }
                if (resolution.type === 'container') {
                    const side = StaticUtilities.getContentResolutionSide(resolution, position);
                    const stackFloating = { type: 'stack', items: [this.props.state.floating], active: 0 };
                    const childFloating = { child: stackFloating, fraction: 0.5 };
                    const childOriginal = { child: resolution.stack, fraction: 0.5 };
                    let newLayout;
                    if (side === 'left') {
                        newLayout = { type: 'row', items: [childFloating, childOriginal] };
                    }
                    else if (side === 'right') {
                        newLayout = { type: 'row', items: [childOriginal, childFloating] };
                    }
                    else if (side === 'top') {
                        newLayout = { type: 'column', items: [childFloating, childOriginal] };
                    }
                    else {
                        newLayout = { type: 'column', items: [childOriginal, childFloating] };
                    }
                    this.replaceInPath(newLayout, path);
                }
                else {
                    const items = resolution.stack.items.slice();
                    items.splice(resolution.index, 0, this.props.state.floating);
                    const newStack = Object.assign(Object.assign({}, resolution.stack), { items, active: resolution.index });
                    this.replaceInPath(newStack, path);
                }
            }
            const newState = {
                layout: path[path.length - 1],
                floating: null
            };
            this.updateState(newState);
            this.unregisterFloatHandlers();
        };
        this.startFloat = (stackItem, options) => {
            if (this.props.state.floating) {
                return;
            }
            const stack = this.findStack(stackItem);
            let newState;
            if (stack) {
                const index = stack.items.indexOf(stackItem);
                const path = this.findPath(stack, this.props.state.layout);
                if (!path) {
                    throw new Error('Stack not found.');
                }
                const items = stack.items.slice();
                items.splice(index, 1);
                const newStack = Object.assign(Object.assign({}, stack), { items, active: Math.min(items.length - 1, stack.active) });
                this.replaceInPath(newStack, path);
                newState = {
                    layout: path[path.length - 1],
                    floating: stack.items[index]
                };
            }
            else {
                newState = {
                    layout: this.props.state.layout,
                    floating: stackItem
                };
            }
            // Update floating position.
            if ('initialPosition' in options) {
                this.setState({ currentMousePosition: options.initialPosition });
            }
            else {
                this.setState({ currentMousePosition: ReactManagedDragable.getPosition(options.event) });
            }
            // Update controlled state.
            this.updateState(newState);
            if ('eventTarget' in options) {
                this.registerFloatHandlers(options.eventTarget);
            }
            else if ('event' in options) {
                if (window.TouchEvent && options.event instanceof TouchEvent && options.event.target && options.event.target instanceof HTMLElement) {
                    // Touch events should be re-registered with their event target, otherwise the touchmove and touchup events will not fire.
                    this.registerFloatHandlers(options.event.target);
                }
                else {
                    // Mouse events can happily be attached to the body.
                    this.registerFloatHandlers(document.body);
                }
            }
            else {
                this.registerFloatHandlers(document.body);
            }
        };
        this.getRowMinWidth = () => {
            return this.props.rowMinWidth === undefined ? 0 : this.props.rowMinWidth;
        };
        this.getColumnMinHeight = () => {
            return this.props.columnMinHeight === undefined ? 0 : this.props.columnMinHeight;
        };
        this.getLayout = () => {
            return this.props.state.layout;
        };
        this.findStack = (stackItem) => {
            return StaticUtilities.findStack(stackItem, this.getLayout());
        };
    }
    componentDidMount() {
        this.updateRootDropArea();
        if (this.props.state.floating) {
            this.registerFloatHandlers(document.body);
        }
        document.body.appendChild(this.portal);
    }
    componentWillUnmount() {
        if (this.raf !== undefined) {
            window.cancelAnimationFrame(this.raf);
        }
        this.unregisterFloatHandlers();
        document.body.removeChild(this.portal);
    }
    render() {
        return React.createElement(React.Fragment, null,
            this.renderLayout(),
            this.renderFloating());
    }
    renderLayout() {
        if (this.props.state.layout) {
            return React.createElement(this.props.renderers.layoutRenderer, { floatyRenderers: this.props.renderers, floatyManager: this, layout: this.props.state.layout });
        }
        else {
            return React.createElement("div", null);
        }
    }
    renderDropResolution() {
        if (!this.state.currentMousePosition) {
            return;
        }
        const resolution = this.getCandidateDropResolution(this.state.currentMousePosition);
        if (!resolution) {
            return;
        }
        if (resolution.type === 'container') {
            const side = StaticUtilities.getContentResolutionSide(resolution, this.state.currentMousePosition);
            const sideDropArea = {
                left: side === 'right' ? resolution.dropArea.left + resolution.dropArea.width * 0.5 : resolution.dropArea.left,
                top: side === 'bottom' ? resolution.dropArea.top + resolution.dropArea.height * 0.5 : resolution.dropArea.top,
                width: side === 'left' || side === 'right' ? resolution.dropArea.width * 0.5 : resolution.dropArea.width,
                height: side === 'top' || side === 'bottom' ? resolution.dropArea.height * 0.5 : resolution.dropArea.height
            };
            return React.createElement(this.props.renderers.dropAreaRenderer, { floatyManager: this, dropArea: sideDropArea });
        }
        else {
            return React.createElement(this.props.renderers.dropAreaRenderer, { floatyManager: this, dropArea: resolution.dropArea });
        }
    }
    renderFloating() {
        if (this.props.state.floating && this.state.currentMousePosition) {
            return ReactDOM.createPortal(React.createElement(React.Fragment, null,
                this.renderDropResolution(),
                React.createElement("div", { style: { position: 'fixed', top: this.state.currentMousePosition.y, left: this.state.currentMousePosition.x } },
                    React.createElement(this.props.renderers.floatingRenderer, { floatyRenderers: this.props.renderers, floatyManager: this, floating: this.props.state.floating }))), this.portal);
        }
    }
    getCandidateDropResolution(position) {
        if (this.props.state.floating) {
            for (const resolution of this.state.dropResolutions) {
                if (DropModel.pointInDropArea(resolution.dropArea, position)) {
                    return resolution;
                }
            }
            if (this.state.dropResolutions.length <= 0 && this.state.rootDropArea && DropModel.pointInDropArea(this.state.rootDropArea, position)) {
                return { type: 'root', dropArea: this.state.rootDropArea };
            }
        }
        return null;
    }
    registerFloatHandlers(eventTarget) {
        if (this.eventTarget) {
            return;
        }
        eventTarget.addEventListener('mousemove', this.handleMove, { passive: false });
        eventTarget.addEventListener('touchmove', this.handleMove, { passive: false });
        eventTarget.addEventListener('mouseup', this.handleUp, { passive: false });
        eventTarget.addEventListener('touchend', this.handleUp, { passive: false });
        this.eventTarget = eventTarget;
    }
    unregisterFloatHandlers() {
        if (!this.eventTarget) {
            return;
        }
        this.eventTarget.removeEventListener('mousemove', this.handleMove);
        this.eventTarget.removeEventListener('touchmove', this.handleMove);
        this.eventTarget.removeEventListener('mouseup', this.handleUp);
        this.eventTarget.removeEventListener('touchend', this.handleUp);
        this.eventTarget = null;
    }
    updateState(state) {
        this.props.onStateChange(Object.assign(Object.assign({}, state), { layout: StaticUtilities.minimizeLayout(state.layout) }));
    }
    onLayoutChange(layout) {
        const newState = Object.assign(Object.assign({}, this.props.state), { layout });
        this.updateState(newState);
    }
    updateDropResolutions() {
        const dropResolutions = [];
        for (const dropResolution of this.dropResolutions.values()) {
            dropResolutions.push(...dropResolution);
        }
        this.setState({ dropResolutions });
    }
    replaceInPath(target, path) {
        let previous = path[0];
        path[0] = target;
        for (let i = 1; i < path.length; i++) {
            // The first item of the path can be a Stack, but the rest are always Columns and Rows.
            const node = path[i];
            const index = node.items.findIndex((item) => item.child === previous);
            const items = node.items.slice();
            items[index] = Object.assign(Object.assign({}, items[index]), { child: path[i - 1] });
            previous = path[i];
            path[i] = Object.assign(Object.assign({}, node), { items });
        }
    }
    findPath(target, from) {
        if (from === null) {
            return null;
        }
        if (target === from) {
            return [target];
        }
        switch (from.type) {
            case 'column':
            case 'row': {
                for (const item of from.items) {
                    const found = this.findPath(target, item.child);
                    if (found) {
                        return [...found, from];
                    }
                }
                break;
            }
        }
        return null;
    }
}
//# sourceMappingURL=floaty.js.map